<template>
    <section class="py-16 lg:py-[150px] px-5 lg:px-[120px]" :class="backgroundColor">
        <div class="mx-auto">
            <!-- Upper Section: 2 Columns -->
            <div class="lg:grid grid-cols-1 lg:grid-cols-2 gap-[50px] flex flex-col lg:flex-col-reverse">
                <!-- Left Column: Text -->
                <div class="flex flex-col">
                    <!-- Headline -->
                    <h2 class="text-[28px] md:text-4xl lg:text-[44px] lg:leading-[57px] font-semibold" :style="{ color: titleColor }">
                        <span v-html="renderTitle()"></span>
                    </h2>

                    <!-- Intro Paragraph (Bold) -->
                    <p class="text-[18px] mt-4 font-pro" :style="{ color: introTextColor }">
                        {{ introText }}
                    </p>

                    <!-- Additional Paragraphs -->
                    <div class="mt-4 space-y-4">
                        <p
                            v-for="(paragraph, index) in paragraphs"
                            :key="index"
                            class="text-[18px] leading-relaxed font-pro"
                            :style="{ color: paragraphColor }"
                            v-html="paragraph"
                        ></p>
                    </div>

                    <div v-if="multipleButtons && multipleButtons.length" class="flex flex-col gap-4 mt-6">
                        <router-link
                            v-for="(button, index) in multipleButtons"
                            :key="index"
                            :to="button.link"
                            class="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition text-xs lg:text-sm w-fit"
                            :style="{ background: button.backgroundColor, color: button.textColor }"
                        >
                            {{ button.text }}
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </router-link>
                    </div>
                </div>

                <!-- Right Column: Photo + Badge -->
                <div class="relative rounded-2xl overflow-hidden min-h-[200px] lg:min-h-[500px]">
                    <!-- Photo -->
                    <img v-if="image" :src="$asset(image, 'public')" :alt="imageAlt" class="w-full h-full object-cover object-top rounded-lg" />

                    <!-- Badge (Top Left) -->
                    <div
                        class="absolute top-[200px] left-1/2 transform -translate-x-1/2 lg:top-4 lg:left-4 lg:translate-x-0 w-[80%] lg:w-36 h-10 lg:h-36 rounded-full flex flex-row gap-2 lg:gap-0 lg:flex-col items-center justify-center text-white bg-[#006CFF]"
                    >
                        <span :class="[badgeSizeType === 1 ? 'text-xl' : 'text-xs']" class="font-semibold text-black">{{ badgeTopText }}</span>
                        <span :class="[badgeSizeType === 1 ? 'text-5xl' : 'text-3xl']" class="font-bold leading-none">{{ badgeMainText }}</span>
                        <span :class="[badgeSizeType === 1 ? 'text-md' : 'text-2xl']" class="font-semibold text-black leading-none">{{ badgeBottomText }}</span>
                    </div>
                </div>
            </div>

            <!-- Lower Section: 4 Feature Columns -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-[50px]">
                <div v-for="(feature, index) in features" :key="index" class="flex flex-col items-start gap-[20px] bg-white p-[30px] rounded-lg shadow-sm">
                    <!-- Checkmark Icon -->
                    <svg class="h-10 w-full" :class="checkmarkColor" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>

                    <!-- Title -->
                    <h4 class="text-[24px] font-semibold text-center w-full" :class="featureTitleColor" v-html="feature.title"></h4>

                    <!-- Subtitle -->
                    <p class="text-[18px] text-center w-full font-pro" :style="{ color: featureSubtitleColor }" v-html="feature.subtitle"></p>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
const props = defineProps({
    backgroundColor: {
        type: String,
        default: '#ffffff'
    },
    title: {
        type: String,
        required: true
    },
    titleHighlight: {
        type: String,
        default: ''
    },
    titleHighlightColor: {
        type: String,
        default: '#0d9488'
    },
    titleHighlightGradientFrom: {
        type: String,
        default: ''
    },
    titleHighlightGradientTo: {
        type: String,
        default: ''
    },
    titleColor: {
        type: String,
        default: '#1a1a1a'
    },
    introText: {
        type: String,
        default: ''
    },
    introTextColor: {
        type: String,
        default: '#4b5563'
    },
    paragraphs: {
        type: Array,
        default: () => []
    },
    paragraphColor: {
        type: String,
        default: '#4b5563'
    },
    image: {
        type: String,
        default: ''
    },
    imageAlt: {
        type: String,
        default: 'Team Photo'
    },
    badgeTopText: {
        type: String,
        default: 'Über'
    },
    badgeMainText: {
        type: String,
        default: '70%'
    },
    badgeBottomText: {
        type: String,
        default: 'Trefferquote'
    },
    badgeSizeType: {
        type: Number,
        default: 1
    },
    features: {
        type: Array,
        required: true
    },
    featureTitleColor: {
        type: String,
        default: '#0d9488'
    },
    featureSubtitleColor: {
        type: String,
        default: '#4b5563'
    },
    checkmarkColor: {
        type: String,
        default: '#0d9488'
    },
    multipleButtons: {
        type: Array,
        default: () => []
    }
});

const renderTitle = () => {
    if (!props.titleHighlight) return props.title;

    const parts = props.title.split(props.titleHighlight);

    if (parts.length === 1) return props.title;

    const highlightStyle =
        props.titleHighlightGradientFrom && props.titleHighlightGradientTo
            ? `background: linear-gradient(to top, ${props.titleHighlightGradientFrom}, ${props.titleHighlightGradientTo}); -webkit-background-clip: text; -webkit-text-fill-color: transparent;`
            : `color: ${props.titleHighlightColor};`;

    return parts
        .map((part, index) => {
            if (index === 0) return part;

            return `<span style="${highlightStyle}">${props.titleHighlight}</span>${part}`;
        })
        .join('');
};
</script>

<style scoped>
/* Additional styles if needed */
</style>
